import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import PayslipTemplate from "./PayslipTemplate";
import "../../ComponentsCss/UserPayslip/UserPayslip.css";
import { FaDownload } from "react-icons/fa";

const MONTHS = [
  { value: "", label: "Select Month" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const textEncoder = new TextEncoder();

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, index) => currentYear - 2 + index);
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const sanitizeFileName = (value) =>
  String(value || "N-A")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "-");

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const getCrc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatUint8Arrays = (arrays) => {
  const totalLength = arrays.reduce((sum, item) => sum + item.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;

  arrays.forEach((item) => {
    output.set(item, offset);
    offset += item.length;
  });

  return output;
};

const createZipBlob = (files) => {
  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = textEncoder.encode(file.name);
    const dataBytes = file.data;
    const crc = getCrc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, 0, true);
    localView.setUint16(12, 0, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, dataBytes.length, true);
    localView.setUint32(22, dataBytes.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    localHeader.set(nameBytes, 30);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, 0, true);
    centralView.setUint16(14, 0, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, dataBytes.length, true);
    centralView.setUint32(24, dataBytes.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    centralHeader.set(nameBytes, 46);

    localParts.push(localHeader, dataBytes);
    centralParts.push(centralHeader);
    offset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatUint8Arrays(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, offset, true);

  return new Blob(
    [concatUint8Arrays([...localParts, centralDirectory, endRecord])],
    { type: "application/zip" }
  );
};

const getPayslipResponse = (companyDetails, employee) => ({
  companyDetails,
  employeeDetails: employee.employeeDetails || {},
  payslips: employee.payslips || [],
});

const getEmployeeName = (employee) =>
  employee.employeeDetails?.employee_details?.name ||
  employee.payslips?.[0]?.employeeName ||
  "N/A";

const getEmployeeId = (employee) =>
  employee.employeeDetails?.employee_id || employee.payslips?.[0]?.employeeId;

const UserPayslip = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [companyDetails, setCompanyDetails] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [capturePayslip, setCapturePayslip] = useState(null);
  const captureRef = useRef(null);

  const years = useMemo(generateYears, []);
  const monthName =
    MONTHS.find((item) => Number(item.value) === Number(month))?.label || month;

  const fetchPayslips = async () => {
    if (!year || !month) {
      setEmployees([]);
      return;
    }

    setLoading(true);

    try {
      const API_URL = `${process.env.REACT_APP_API_URL}/payslip/all_payslips`;
      const response = await axios.get(API_URL, {
        params: { year, month },
      });

      if (response.data?.success) {
        setCompanyDetails(response.data.data?.companyDetails || {});
        setEmployees(response.data.data?.employees || []);
      } else {
        setCompanyDetails({});
        setEmployees([]);
      }
    } catch (err) {
      console.error("Error fetching all payslips:", err);
      setCompanyDetails({});
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayslips();
  }, [year, month]);

  const createPdfBytes = async (payslipResponse) => {
    setCapturePayslip(payslipResponse);
    await delay(250);

    if (!captureRef.current) {
      throw new Error("Payslip template was not rendered.");
    }

    const canvas = await html2canvas(captureRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdfDoc = await PDFDocument.create();
    const pngImage = await pdfDoc.embedPng(imgData.split(",")[1]);
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    const imageScale = Math.min(
      pageWidth / pngImage.width,
      pageHeight / pngImage.height
    );
    const scaledWidth = pngImage.width * imageScale;
    const scaledHeight = pngImage.height * imageScale;

    page.drawImage(pngImage, {
      x: (pageWidth - scaledWidth) / 2,
      y: pageHeight - scaledHeight,
      width: scaledWidth,
      height: scaledHeight,
    });

    return pdfDoc.save();
  };

  const getPdfFileName = (employee) => {
    const employeeId = sanitizeFileName(getEmployeeId(employee));
    const employeeName = sanitizeFileName(getEmployeeName(employee));
    return `${employeeId}-${employeeName}-payslip.pdf`;
  };

  const handleDownloadSingle = async (employee) => {
    setDownloading(true);
    try {
      const payslipResponse = getPayslipResponse(companyDetails, employee);
      const pdfBytes = await createPdfBytes(payslipResponse);
      downloadBlob(
        new Blob([pdfBytes], { type: "application/pdf" }),
        getPdfFileName(employee)
      );
    } catch (err) {
      console.error("Error downloading payslip:", err);
    } finally {
      setCapturePayslip(null);
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!employees.length) return;

    setDownloading(true);

    try {
      const folderName = `${sanitizeFileName(monthName)}-${year}-payslips`;
      const files = [];

      for (const employee of employees) {
        if (!employee.payslips?.length) continue;

        const payslipResponse = getPayslipResponse(companyDetails, employee);
        const pdfBytes = await createPdfBytes(payslipResponse);

        files.push({
          name: `${folderName}/${getPdfFileName(employee)}`,
          data: new Uint8Array(pdfBytes),
        });
      }

      if (!files.length) return;

      const zipBlob = createZipBlob(files);
      downloadBlob(zipBlob, `${folderName}.zip`);
    } catch (err) {
      console.error("Error downloading all payslips:", err);
    } finally {
      setCapturePayslip(null);
      setDownloading(false);
    }
  };

  return (
    <div className="payslip-container">
      <div className="user-payslip-header">
        <h2 className="payslip-title">Users Payslips</h2>
        <button
          className="download-all-btn"
          onClick={handleDownloadAll}
          disabled={loading || downloading || employees.length === 0}
        >
          <FaDownload />
          {downloading ? "Preparing..." : "Download All"}
        </button>
      </div>

      <div className="payslip-label-select">
        <label className="payslip-label">Select Year:</label>
        <select
          className="payslip-select"
          value={year}
          onChange={(e) => setYear(e.target.value)}
        >
          <option value="">Select Year</option>
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>

        <label className="payslip-label">Select Month:</label>
        <select
          className="payslip-select"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {MONTHS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="user-payslip-table-container">
        <table className="user-payslip-table">
          <thead>
            <tr>
              <th>Name (Emp ID)</th>
              <th>Designation</th>
              <th>Month</th>
              <th>Working Days</th>
              <th>Net Pay</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading payslips...</td>
              </tr>
            ) : employees.length > 0 ? (
              employees.map((employee) => {
                const payslip = employee.payslips?.[0] || {};
                const employeeId = getEmployeeId(employee);

                return (
                  <tr key={payslip._id || employeeId}>
                    <td>
                      {getEmployeeName(employee)} ({employeeId || "N/A"})
                    </td>
                    <td>
                      {employee.employeeDetails?.official_details
                        ?.designation || "N/A"}
                    </td>
                    <td>{monthName}</td>
                    <td>{payslip.payrollSummary?.payableDays || 0}</td>
                    <td>₹{Number(payslip.netPay || 0).toFixed(2)}</td>
                    <td className="action">
                      <button
                        className="icon-btn icon download"
                        onClick={() => handleDownloadSingle(employee)}
                        title="Download PDF"
                        disabled={downloading}
                      >
                        <FaDownload />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6">No payslips found for {monthName} {year}.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {capturePayslip && (
        <div className="payslip-hidden-capture">
          <PayslipTemplate
            payslipResponse={capturePayslip}
            showDownloadButton={false}
            wrapperClassName="payslip-capture-wrapper"
            templateRef={captureRef}
          />
        </div>
      )}
    </div>
  );
};

export default UserPayslip;
